// fronted/src/pages/WalletRisk.js
import React, { useState, useEffect } from "react";
import axios from "../utils/axiosConfig";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import API_URL from "../config";
import { FaMagnifyingGlass } from "react-icons/fa6";
import { toast } from "react-toastify";
import WalletRiskReport from "../components/walletRisk/WalletRiskReport";
import WalletRiskSkeleton from "../components/walletRisk/WalletRiskSkeleton";
import WalletRiskInfoPanel from "../components/walletRisk/WalletRiskInfoPanel";

const LOADING_STEPS = ["platform", "labels", "sanction", "compliance"];

function WalletRisk() {
  const { t } = useTranslation();
  const location = useLocation();

  const [walletAddress, setWalletAddress] = useState("");
  const [chainId, setChainId] = useState(1);
  const [report, setReport] = useState(null);
  const [comment, setComment] = useState("");
  const [label, setLabel] = useState("scam");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [rateLimitRemaining, setRateLimitRemaining] = useState(null);

  const chainAddressRules = {
    '-2': {
      regex: /^T[1-9A-HJ-NP-Za-km-z]{33}$/,
      name: 'TRON',
      priority: 1,
    },
    '-1': {
      regex: /^(1[1-9A-HJ-NP-Za-km-z]{25,34}|3[1-9A-HJ-NP-Za-km-z]{25,34}|bc1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{39})$/,
      name: 'Bitcoin',
      priority: 2,
      exclude: /^T[1-9A-HJ-NP-Za-km-z]{33}$/,
    },
    '-3': {
      regex: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/,
      name: 'Solana',
      priority: 3,
      exclude: /^(T[1-9A-HJ-NP-Za-km-z]{33}|bc1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{39})$/,
    },
    evm: {
      regex: /^0x[a-fA-F0-9]{40}$/,
      name: 'EVM Compatible',
      priority: 4,
      chains: [{ chainId: '1', name: 'Ethereum' }],
    },
  };

  const detectChain = (address) => {
    if (!address) return 1;
    for (const [id, rule] of Object.entries(chainAddressRules)) {
      const matches = rule.regex.test(address);
      const excluded = rule.exclude ? rule.exclude.test(address) : false;
      if (matches && !excluded) {
        if (id === 'evm') {
          return parseInt(rule.chains[0].chainId, 10);
        }
        return parseInt(id, 10);
      }
    }
    return 1;
  };

  useEffect(() => {
    async function checkAuth() {
      try {
        const response = await axios.get(`${API_URL}/api/current_user`, { withCredentials: true });
        if (response.data) {
          setIsAuthenticated(true);
          setUserId(response.data._id);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        setIsAuthenticated(false);
      }
    }
    checkAuth();
  }, []);

  useEffect(() => {
    const detectedChainId = detectChain(walletAddress);
    setChainId(detectedChainId);
  }, [walletAddress]);

  const queryWalletRisk = async (address, chain) => {
    if (!address) return;

    setLoading(true);
    setLoadingStep(0);
    setHasSearched(true);
    setReport(null);

    const stepTimer = setInterval(() => {
      setLoadingStep((prev) => (prev < LOADING_STEPS.length - 1 ? prev + 1 : prev));
    }, 700);

    try {
      const response = await axios.post(
        `${API_URL}/api/wallet/risk-check`,
        { walletAddress: address, chain_id: chain },
        { withCredentials: true }
      );

      const remaining = response.headers['x-ratelimit-remaining'];
      if (remaining !== undefined) {
        setRateLimitRemaining(Number(remaining));
      }

      setReport(response.data);
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error(error.response.data?.message || t("rateLimitExceeded"));
      } else {
        toast.error(t("queryFailed"));
      }
      setReport(null);
    } finally {
      clearInterval(stepTimer);
      setLoading(false);
      setLoadingStep(LOADING_STEPS.length);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await queryWalletRisk(walletAddress, chainId);
  };

  useEffect(() => {
    if (location.state && location.state.walletAddress) {
      setWalletAddress(location.state.walletAddress);
      const detectedChainId = detectChain(location.state.walletAddress);
      setChainId(detectedChainId);
      queryWalletRisk(location.state.walletAddress, detectedChainId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleAddToWhitelist = async () => {
    if (!walletAddress) {
      toast.error(t("enterValidWalletAddress"));
      return;
    }
    if (!isAuthenticated) {
      window.location.href = `${API_URL}/api/auth/google`;
      return;
    }
    try {
      await axios.post(`${API_URL}/api/whitelist`, { walletAddress, userId, comment });
      toast.success(t("walletAddedToWhitelist"));
    } catch (error) {
      toast.error(t("errorAddingWalletToWhitelist"));
    }
  };

  const handleAddToBlacklist = async () => {
    if (!walletAddress) {
      toast.error(t("enterValidWalletAddress"));
      return;
    }
    if (!isAuthenticated) {
      window.location.href = `${API_URL}/api/auth/google`;
      return;
    }
    try {
      await axios.post(`${API_URL}/api/blacklist`, {
        walletAddress,
        riskLevel: "low",
        blackScore: 1,
        labels: [label],
        comment,
        userId,
      });

      const userResponse = await axios.post(`${API_URL}/api/add_suspicious_wallet_to_user`, {
        userId,
        walletAddress,
        label,
        comment,
      });

      if (userResponse.status === 200) {
        toast.success(t("walletAddedToBlacklist"));
      }
    } catch (error) {
      if (error.response && error.response.status === 400 && error.response.data.error === "已經回報過該地址") {
        toast.error(error.response.data.error);
      } else {
        toast.error(t("errorAddingWalletToBlacklist"));
      }
    }
  };

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 min-h-screen">
      <div
        className="w-full h-64 bg-cover bg-center flex flex-col items-center justify-center text-white text-3xl font-bold shadow-lg"
        style={{ backgroundImage: "url('/images/myReportsBanner.png')" }}
      >
        <h1 className="text-white uppercase tracking-wide">{t("walletRiskQuery")}</h1>
        <p className="text-lg font-medium mt-2 text-gray-200">{t("walletRiskAssessment")}</p>
      </div>

      <div className="flex justify-center items-center py-12 px-4">
        <form onSubmit={handleSubmit} className="w-full max-w-2xl">
          <div className="flex h-12 rounded-xl shadow-md border border-purple-200/50 dark:border-purple-700/30 overflow-hidden">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder={t("inputWalletAddress")}
                value={walletAddress}
                onChange={(e) => setWalletAddress(e.target.value)}
                className="w-full px-4 py-2 pl-10 bg-white dark:bg-gray-800 text-black dark:text-white border-none focus:outline-none focus:ring-2 focus:ring-purple-300 dark:focus:ring-violet-500 placeholder-gray-400 dark:placeholder-gray-500 h-full box-border leading-[2rem] transition-all"
              />
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaMagnifyingGlass className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </span>
            </div>
            <select
              value={chainId}
              onChange={(e) => setChainId(parseInt(e.target.value, 10))}
              className="px-3 py-2 bg-white dark:bg-gray-800 text-black dark:text-white border-l border-purple-200/50 dark:border-purple-700/30 focus:outline-none focus:ring-2 focus:ring-purple-300 dark:focus:ring-violet-500 h-full box-border leading-[2rem] transition-all"
            >
              <option value="-3">Solana</option>
              <option value="-2">TRON</option>
              <option value="-1">Bitcoin</option>
              <option value="1">Ethereum</option>
              <option value="10">Optimism</option>
              <option value="25">Cronos</option>
              <option value="56">BSC</option>
              <option value="100">Gnosis</option>
              <option value="137">Polygon</option>
              <option value="169">Manta Pacific</option>
              <option value="199">Bittorrent</option>
              <option value="250">Fantom</option>
              <option value="288">Boba</option>
              <option value="324">zkSync Era</option>
              <option value="1024">CLV</option>
              <option value="1101">Polygon zkEVM</option>
              <option value="1111">Wemix</option>
              <option value="1284">Moonbeam</option>
              <option value="1285">Moonriver</option>
              <option value="5000">Mantle</option>
              <option value="8453">Base</option>
              <option value="42161">Arbitrum</option>
              <option value="42220">Celo</option>
              <option value="43114">Avalanche</option>
              <option value="59144">Linea</option>
              <option value="81457">Blast</option>
              <option value="1313161554">Aurora</option>
            </select>
            <button
              type="submit"
              disabled={loading}
              className="px-6 bg-gradient-to-r from-purple-400 to-indigo-500 dark:from-purple-500 dark:to-violet-600 text-white font-bold rounded-r-xl hover:from-purple-500 hover:to-indigo-600 dark:hover:from-purple-600 dark:hover:to-violet-700 focus:outline-none focus:ring-2 focus:ring-purple-300 dark:focus:ring-violet-500 h-full box-border leading-[2rem] flex items-center justify-center transition-all whitespace-nowrap disabled:opacity-60"
            >
              {loading ? t("searching") : t("search")}
            </button>
          </div>
          {rateLimitRemaining !== null && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
              {t("rateLimitRemaining", { count: rateLimitRemaining })}
            </p>
          )}
        </form>
      </div>

      <div className="container mx-auto px-4 pb-16 max-w-6xl">
        {loading && (
          <div className="mb-6">
            <div className="flex flex-wrap gap-2 justify-center mb-6">
              {LOADING_STEPS.map((step, index) => (
                <span
                  key={step}
                  className={`text-xs px-3 py-1 rounded-full border ${
                    index <= loadingStep
                      ? "bg-purple-100 dark:bg-purple-900/40 border-purple-300 dark:border-purple-600 text-purple-700 dark:text-purple-200"
                      : "border-gray-300 dark:border-gray-600 text-gray-400"
                  }`}
                >
                  {t(`loadingStep_${step}`)}
                </span>
              ))}
            </div>
            <WalletRiskSkeleton />
          </div>
        )}

        {!loading && report && (
          <WalletRiskReport
            report={report}
            walletAddress={walletAddress}
            chainId={chainId}
            t={t}
          />
        )}

        {!loading && hasSearched && !report && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            {t("noWalletDataYet")}
          </div>
        )}

        {report && !loading && (
          <>
            <div className="mt-8 bg-white dark:bg-gray-800 rounded-xl border border-purple-200/50 dark:border-purple-700/30 p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{t("reportActions")}</h3>
              <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:space-x-4">
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t("enterComment")}
                  className="w-full md:flex-1 p-3 border rounded-xl dark:bg-gray-800 dark:text-white dark:border-gray-700 resize-none border-purple-200/50 dark:border-purple-700/30 focus:ring-2 focus:ring-purple-300 dark:focus:ring-violet-500 transition-all"
                />
                <button
                  onClick={handleAddToWhitelist}
                  className="bg-gradient-to-r from-green-400 to-green-600 dark:from-green-500 dark:to-green-700 text-white px-4 py-2 rounded-xl shadow-md hover:from-green-500 hover:to-green-700 dark:hover:from-green-600 dark:hover:to-green-800 focus:ring-2 focus:ring-purple-300 dark:focus:ring-violet-500 transition-all"
                >
                  {t("thisIsMyWallet")}
                </button>
                <button
                  onClick={handleAddToBlacklist}
                  className="bg-gradient-to-r from-red-400 to-red-600 dark:from-red-500 dark:to-red-700 text-white px-4 py-2 rounded-xl shadow-md hover:from-red-500 hover:to-red-700 dark:hover:from-red-600 dark:hover:to-red-800 focus:ring-2 focus:ring-purple-300 dark:focus:ring-violet-500 transition-all"
                >
                  {t("thisIsSuspiciousWallet")}
                </button>
              </div>
            </div>
            <WalletRiskInfoPanel
              t={t}
              isAuthenticated={isAuthenticated}
              rateLimitRemaining={rateLimitRemaining}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default WalletRisk;
